import React, { useEffect, useState } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    Tabs, TabList, TabPanels, Tab, TabPanel, Box, Text, Flex, SimpleGrid,
    Badge, Image, Spinner, useColorModeValue, HStack, Card, CardBody, Stack, Icon, Divider
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiUser, FiPhone, FiMail, FiMapPin, FiEye, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import { BsCart4, BsBoxSeam, BsCheckCircleFill, BsXCircleFill, BsClockFill, BsQuestionCircleFill } from 'react-icons/bs';
import { IoMdPricetags } from 'react-icons/io';
import { MdPhoneDisabled, MdLocalShipping, MdInventory2, MdReceiptLong, MdVerifiedUser } from 'react-icons/md';
import { TbTruckDelivery } from 'react-icons/tb';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import axiosClient from '../axios-client';
import { useStateContext } from '../context/ContextProvider';

// ─────────────────────────────────────────────────────────────────
//  Business rule: cascade statuses so empty fields show meaningful
//  default values based on the progression of the order.
// ─────────────────────────────────────────────────────────────────
const resolveStatuses = (order) => {
    if (!order) return { confirmation: null, stock: null, delivery: null, facture: null };

    const confirmation = order.status_confirmation || null;

    // If confirmed → stock starts at "Nouveau" even if empty in DB
    const stockRaw = order.status_stock || null;
    const stock = confirmation === 'Confirmé'
        ? (stockRaw || 'Nouveau')
        : stockRaw;

    // If stock collected → delivery starts at "Nouveau" even if empty in DB
    const deliveryRaw = order.status_delivery || null;
    const delivery = stock === 'Collecté'
        ? (deliveryRaw || 'Nouveau')
        : deliveryRaw;

    // If delivered → facture starts at "Non payé" even if empty in DB
    const factureRaw = order.status_facture || null;
    const facture = delivery === 'Livré'
        ? (factureRaw || 'Non payé')
        : factureRaw;

    return { confirmation, stock, delivery, facture };
};

// ─────────────────────────────────────────────────────────────────
//  Color + icon mapping per status value
// ─────────────────────────────────────────────────────────────────
const STATUS_META = {
    // Confirmation
    'Confirmé':       { color: 'green',  icon: BsCheckCircleFill },
    'Mauvais numéro': { color: 'red',    icon: MdPhoneDisabled },
    'Annulé':         { color: 'red',    icon: BsXCircleFill },
    'Refusé':         { color: 'red',    icon: BsXCircleFill },
    'Nouveau':        { color: 'gray',   icon: BsClockFill },
    // Stock
    'Collecté':       { color: 'teal',   icon: MdInventory2 },
    'Épuisé':         { color: 'orange', icon: FiAlertCircle },
    // Delivery
    'Livré':          { color: 'green',  icon: TbTruckDelivery },
    'En cours':       { color: 'blue',   icon: TbTruckDelivery },
    // Facture
    'Payé':           { color: 'green',  icon: RiMoneyDollarCircleFill },
    'Non payé':       { color: 'orange', icon: RiMoneyDollarCircleFill },
};

const getMeta = (value) => STATUS_META[value] || { color: 'gray', icon: BsQuestionCircleFill };

const COLOR_MAP = {
    green:  { bg: 'green.50',  dark: 'green.900',  text: 'green.600',  darkText: 'green.300',  badge: 'green' },
    red:    { bg: 'red.50',    dark: 'red.900',    text: 'red.600',    darkText: 'red.300',    badge: 'red' },
    gray:   { bg: 'gray.50',   dark: 'gray.700',   text: 'gray.500',   darkText: 'gray.400',   badge: 'gray' },
    teal:   { bg: 'teal.50',   dark: 'teal.900',   text: 'teal.600',   darkText: 'teal.300',   badge: 'teal' },
    orange: { bg: 'orange.50', dark: 'orange.900', text: 'orange.600', darkText: 'orange.300', badge: 'orange' },
    blue:   { bg: 'blue.50',   dark: 'blue.900',   text: 'blue.600',   darkText: 'blue.300',   badge: 'blue' },
};

// ─────────────────────────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────────────────────────
function PreviewOrderModal({ isOpen, onClose, orderId }) {
    const { t } = useTranslation();
    const { user } = useStateContext();

    // Theme Colors
    const bgContent  = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg   = useColorModeValue('gray.50', 'whiteAlpha.100');
    const cardBg     = useColorModeValue('white', 'gray.700');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const badgeBg    = useColorModeValue('blue.50', 'blue.900');
    const badgeColor = useColorModeValue('blue.600', 'blue.200');
    const isDark     = useColorModeValue(false, true);

    // Loading & Order State
    const [loading, setLoading] = useState(false);
    const [detailOrder, setDetailOrder] = useState(null);
    const [productsOrderData, setProductsOrderData] = useState([]);

    // Fetch Order Details
    const fetchOrderData = async () => {
        if (!orderId || !isOpen) return;
        setLoading(true);
        try {
            const [orderRes, prodRes] = await Promise.all([
                axiosClient.get(`commandes/${orderId}/`),
                axiosClient.get(`/produitCommande/getProductsOrder/?commande=${orderId}&client_id=${user?.id}&userRole=${user?.role}`)
            ]);
            setDetailOrder(orderRes.data);
            setProductsOrderData(prodRes.data || []);
        } catch (error) {
            console.error('Error fetching order details for Preview:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && orderId) {
            fetchOrderData();
        }
    }, [isOpen, orderId]);

    // Resolve statuses with cascade logic
    const { confirmation, stock, delivery, facture } = resolveStatuses(detailOrder);

    // ── Status pipeline card ──────────────────────────────────────
    const StatusCard = ({ labelKey, value, tabIcon: TabIcon, isLast }) => {
        const displayValue = value || null;
        const meta = getMeta(displayValue);
        const colors = COLOR_MAP[meta.color] || COLOR_MAP.gray;
        const StatusIcon = meta.icon;
        const bgCard = isDark ? colors.dark : colors.bg;
        const textCol = isDark ? colors.darkText : colors.text;

        return (
            <Flex flex="1" direction="column" align="center" position="relative">
                {/* Connector line between cards */}
                {!isLast && (
                    <Box
                        display={{ base: 'none', md: 'block' }}
                        position="absolute"
                        top="28px"
                        right="-50%"
                        width="100%"
                        height="2px"
                        bg={displayValue ? colors.badge + '.300' : 'gray.200'}
                        zIndex={0}
                    />
                )}

                {/* Icon circle */}
                <Flex
                    w={14} h={14}
                    rounded="full"
                    bg={bgCard}
                    border="3px solid"
                    borderColor={textCol}
                    align="center"
                    justify="center"
                    color={textCol}
                    mb={3}
                    zIndex={1}
                    shadow="md"
                >
                    <StatusIcon size={24} />
                </Flex>

                {/* Label */}
                <Text fontSize="11px" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.05em" mb={1}>
                    {t(labelKey)}
                </Text>

                {/* Badge */}
                <Badge
                    colorScheme={displayValue ? colors.badge : 'gray'}
                    rounded="full"
                    px={3} py={1}
                    fontSize="11px"
                    fontWeight="700"
                    textAlign="center"
                >
                    {displayValue ? t(displayValue) : '—'}
                </Badge>
            </Flex>
        );
    };

    const InfoItem = ({ icon, label, value }) => (
        <Flex align="flex-start" gap={3}>
            <Box mt={1} color="blue.500">
                <Icon as={icon} size={18} />
            </Box>
            <Box>
                <Text fontSize="xs" color={mutedColor} fontWeight="600">{label}</Text>
                <Text fontSize="sm" fontWeight="700">{value || '-'}</Text>
            </Box>
        </Flex>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside" isCentered>
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
            <ModalContent bg={bgContent} borderColor={borderColor} borderWidth={1} rounded="2xl" shadow="2xl" overflow="hidden">
                <ModalHeader bg={headerBg} py={4} px={6} borderBottomWidth={1} borderColor={borderColor}>
                    <Flex align="center" justify="space-between" pr={6}>
                        <HStack spacing={3}>
                            <Flex align="center" justify="center" w={10} h={10} rounded="xl" bg={badgeBg} color={badgeColor} shadow="sm">
                                <FiEye size={20} />
                            </Flex>
                            <Box>
                                <Text fontSize="lg" fontWeight="800" letterSpacing="tight">
                                    {t("Aperçu de la commande")}
                                </Text>
                                <Text fontSize="xs" color={mutedColor} fontWeight="medium">
                                    {detailOrder?.code ? `${t("Commande")} #${detailOrder.code}` : t("Détails de la commande")}
                                </Text>
                            </Box>
                        </HStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton top={4} right={4} rounded="full" />

                <ModalBody p={6}>
                    {loading ? (
                        <Flex justify="center" align="center" py={16} direction="column" gap={3}>
                            <Spinner size="xl" color="blue.500" thickness="4px" />
                            <Text fontSize="sm" color={mutedColor} fontWeight="medium">{t("Chargement des détails...")}</Text>
                        </Flex>
                    ) : (
                        <Stack spacing={6}>
                            {/* ── Status Pipeline ───────────────────────────── */}
                            <Card bg={headerBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="none">
                                <CardBody py={5} px={6}>
                                    <Text fontSize="xs" fontWeight="800" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb={5}>
                                        {t("Progression de la commande")}
                                    </Text>
                                    <Flex
                                        direction={{ base: 'column', md: 'row' }}
                                        justify="space-between"
                                        align={{ base: 'flex-start', md: 'flex-start' }}
                                        gap={{ base: 4, md: 0 }}
                                        position="relative"
                                    >
                                        <StatusCard
                                            labelKey="Confirmation"
                                            value={confirmation}
                                            tabIcon={MdVerifiedUser}
                                        />
                                        <StatusCard
                                            labelKey="Stock"
                                            value={stock}
                                            tabIcon={MdInventory2}
                                        />
                                        <StatusCard
                                            labelKey="Livraison"
                                            value={delivery}
                                            tabIcon={TbTruckDelivery}
                                        />
                                        <StatusCard
                                            labelKey="Facture"
                                            value={facture}
                                            tabIcon={MdReceiptLong}
                                            isLast
                                        />
                                    </Flex>
                                </CardBody>
                            </Card>

                            {/* ── Tabs: Client info + Products ──────────────── */}
                            <Tabs variant="enclosed" colorScheme="blue" isFitted rounded="xl">
                                <TabList borderColor={borderColor}>
                                    <Tab fontWeight="700" roundedTop="xl" _selected={{ color: 'blue.500', borderColor: borderColor, borderBottomColor: bgContent, bg: bgContent }}>
                                        <HStack spacing={2}>
                                            <FiUser />
                                            <Text>{t("Informations client")}</Text>
                                        </HStack>
                                    </Tab>
                                    <Tab fontWeight="700" roundedTop="xl" _selected={{ color: 'blue.500', borderColor: borderColor, borderBottomColor: bgContent, bg: bgContent }}>
                                        <HStack spacing={2}>
                                            <BsBoxSeam />
                                            <Text>{t("Produits")}</Text>
                                            <Badge ml={1} colorScheme="blue" rounded="full" px={2}>{productsOrderData.length}</Badge>
                                        </HStack>
                                    </Tab>
                                </TabList>

                                <TabPanels>
                                    {/* TAB 1: CUSTOMER INFO */}
                                    <TabPanel p={0} pt={4}>
                                        <Stack spacing={5}>
                                            <Card bg={headerBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="none">
                                                <CardBody py={5} px={6}>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                                        <InfoItem icon={FiUser}   label={t("Nom complet")}  value={detailOrder?.full_name} />
                                                        <InfoItem icon={FiPhone}  label={t("Téléphone")}    value={detailOrder?.tel} />
                                                        <InfoItem icon={FiMail}   label={t("Email")}         value={detailOrder?.email} />
                                                        <InfoItem icon={FiMapPin} label={t("Pays")}          value={detailOrder?.pays} />
                                                        <InfoItem icon={FiMapPin} label={t("Ville")}         value={detailOrder?.ville} />
                                                        <InfoItem icon={FiMapPin} label={t("Région/État")}  value={detailOrder?.region || detailOrder?.state} />
                                                        <InfoItem icon={FiMapPin} label={t("Adresse")}       value={detailOrder?.address} />
                                                        <InfoItem icon={FiMapPin} label={t("Code postal")}  value={detailOrder?.code_postal || detailOrder?.postal_code} />
                                                    </SimpleGrid>
                                                </CardBody>
                                            </Card>
                                        </Stack>
                                    </TabPanel>

                                    {/* TAB 2: PRODUCT INFO */}
                                    <TabPanel p={0} pt={4}>
                                        <Stack spacing={5}>
                                            <Card bg={headerBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="none">
                                                <CardBody py={3} px={4}>
                                                    <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                                                        <HStack spacing={3}>
                                                            <Box p={2.5} bg="blue.500" color="white" rounded="lg">
                                                                <BsCart4 size={18} />
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase">{t("Total des articles")}</Text>
                                                                <Text fontSize="md" fontWeight="800">{productsOrderData.length} {t("produit(s)")}</Text>
                                                            </Box>
                                                        </HStack>
                                                        <HStack spacing={3}>
                                                            <Box p={2.5} bg="green.500" color="white" rounded="lg">
                                                                <IoMdPricetags size={18} />
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <Text fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase">{t("Montant total")}</Text>
                                                                <Text fontSize="md" fontWeight="800" color="green.500">
                                                                    {detailOrder?.price ? parseFloat(detailOrder.price).toFixed(2) : '0.00'}
                                                                </Text>
                                                            </Box>
                                                        </HStack>
                                                    </Flex>
                                                </CardBody>
                                            </Card>

                                            <Stack spacing={3}>
                                                {productsOrderData.map((prod, idx) => (
                                                    <Card key={idx} bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} shadow="sm">
                                                        <CardBody p={4}>
                                                            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                                                                <Flex align="center" gap={3} flex="1" minW="200px">
                                                                    <Image
                                                                        boxSize="50px"
                                                                        objectFit="cover"
                                                                        src={prod.produit?.images?.[0]?.image || ''}
                                                                        fallbackSrc="https://via.placeholder.com/50"
                                                                        rounded="lg"
                                                                        borderWidth={1}
                                                                        borderColor={borderColor}
                                                                    />
                                                                    <Box>
                                                                        <Text fontWeight="700" fontSize="sm" noOfLines={1}>{prod.produit?.title || t("Produit indisponible")}</Text>
                                                                        <HStack spacing={2} mt={1}>
                                                                            {prod.color && <Badge colorScheme="purple" fontSize="10px" rounded="full" px={2}>{prod.color}</Badge>}
                                                                            {prod.size  && <Badge colorScheme="teal"   fontSize="10px" rounded="full" px={2}>{prod.size}</Badge>}
                                                                            {prod.produit?.sku && <Badge colorScheme="gray" fontSize="10px" rounded="full" px={2}>SKU: {prod.produit.sku}</Badge>}
                                                                        </HStack>
                                                                    </Box>
                                                                </Flex>

                                                                <HStack spacing={6} align="center">
                                                                    <Box textAlign="center">
                                                                        <Text fontSize="10px" color={mutedColor} fontWeight="700">{t("QTE")}</Text>
                                                                        <Text fontWeight="800" fontSize="md">{prod.quantity}</Text>
                                                                    </Box>
                                                                    <Box textAlign="right" minW="70px">
                                                                        <Text fontSize="10px" color={mutedColor} fontWeight="700">{t("PRIX")}</Text>
                                                                        <Text fontWeight="800" fontSize="md" color="blue.500">{parseFloat(prod.price || 0).toFixed(2)}</Text>
                                                                    </Box>
                                                                </HStack>
                                                            </Flex>
                                                        </CardBody>
                                                    </Card>
                                                ))}
                                                {productsOrderData.length === 0 && (
                                                    <Flex justify="center" p={5}>
                                                        <Text color={mutedColor} fontSize="sm" fontWeight="600">{t("Aucun produit trouvé.")}</Text>
                                                    </Flex>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Stack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default PreviewOrderModal;
